import { useTheme } from '@chakra-ui/react';
import { useState } from 'react';

import { Td, Th, Tr } from '~/common/components/Tables';
import Text from '~/common/components/Texts/Text';
import { formatDateWithTime } from '~/common/util/format';

import {
  FieldValueDisplay,
  HistoryRowProps,
  IncomeReconAuditTableProps,
  RowTd,
} from './AuditTable';
import { RowDetails } from './RenderingDetails';

type IncomeReconciliationRowProps = {
  loanAction?: string;
  auditData?: IncomeReconAuditTableProps['data'][number]['fields'][number]['value'];
};

export const IncomeReconciliationHeaderContent = (section) => (
  <>
    <Th width="35%">
      {String(section?.type).toLowerCase().includes('employment')
        ? 'Income Type'
        : 'Address'}
    </Th>
    <Th width="10%">Amount</Th>
    <Th width="10%">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Text>Included/</Text>
        <Text>Excluded</Text>
      </div>
    </Th>
    <Th width="20%">User</Th>
    <Th width="15%">Date/Time</Th>
    <Th width="15%">History</Th>
  </>
);

function IncomeReconciliationHistoryRow({
  amount,
  excluded,
  userName,
  userEmail,
  modifiedDateTime,
}: Readonly<HistoryRowProps>) {
  return (
    <Tr data-testid="HistoryRow">
      <Td>&nbsp;</Td>
      <Td>
        <FieldValueDisplay fieldValue={amount} />
      </Td>
      <Td>
        <FieldValueDisplay fieldValue={excluded} />
      </Td>
      <Td>
        <b>{userName ?? 'Username Not Available'}</b>
        <br />
        {userEmail}
      </Td>
      <Td>{formatDateWithTime(modifiedDateTime)}</Td>
      <Td>&nbsp;</Td>
    </Tr>
  );
}

function IncomeReconciliationRow({
  auditData = [],
  loanAction = '',
}: Readonly<IncomeReconciliationRowProps>) {
  const theme = useTheme();
  const [showHistory, setShowHistory] = useState(false);
  const handleClick = () => {
    setShowHistory(!showHistory);
  };

  const [lastEntry, ...restEntries] = auditData;
  const { amount, excluded, userEmail, modifiedDateTime, userName } =
    lastEntry || {};
  const isShowHistoryActive = !!restEntries.length;

  return (
    <>
      <Tr
        data-testid={loanAction}
        sx={{
          borderLeft: '1px solid',
          _hover: { background: theme.colors?.bgGray[300] },
        }}
      >
        <RowTd>
          <strong>{loanAction}</strong>
        </RowTd>
        <RowTd>
          <FieldValueDisplay fieldValue={amount} />
        </RowTd>
        <RowTd>
          <FieldValueDisplay fieldValue={excluded} />
        </RowTd>
        {RowDetails(
          loanAction,
          userName,
          userEmail,
          modifiedDateTime,
          isShowHistoryActive,
          handleClick,
          showHistory,
        )}
      </Tr>
      {showHistory &&
        !!restEntries.length &&
        restEntries.map((entry) => {
          return (
            <IncomeReconciliationHistoryRow
              key={loanAction + `${entry.amount}`}
              {...entry}
            />
          );
        })}
    </>
  );
}

export function RenderIncomeReconciliationRows(entries) {
  return entries.map(({ key, value }) => (
    <IncomeReconciliationRow key={key} loanAction={key} auditData={value} />
  ));
}
